import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Typography,
  FormControlLabel,
  Switch,
  Paper,
  Button,
  Alert,
  Snackbar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { postQuery, fetchData } from "../../../services/BackendServices";
import { useLoading } from "../../../context/LoadingContext";
import { Auth } from "aws-amplify";

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  display: "flex",
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transition: "transform 300ms ease-in-out",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#4caf50",
        opacity: 1,
        border: 0,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
    transition: "all 300ms ease-in-out",
  },
  "& .MuiSwitch-track": {
    borderRadius: 13,
    backgroundColor: "#ccc",
    opacity: 1,
    transition: "background-color 300ms ease-in-out",
  },
}));

const Notification = () => {
  const { setLoading, loading } = useLoading();
  const [emailID, setEmailID] = useState("");

  // Define notification events - wrapped in useMemo to prevent re-creation
  const notificationEvents = useMemo(
    () => [
      {
        key: "sendFirmOffers",
        label: "Send Firm offers",
        event: "SEND_FIRM_OFFERS",
        section: "offers",
      },
      {
        key: "shortlist",
        label: "Shortlist",
        event: "SHORTLIST",
        section: "offers",
      },
      {
        key: "raiseQuery",
        label: "Raise Query",
        event: "RAISE_QUERY",
        section: "offers",
      },
      {
        key: "sendBAFO",
        label: "Send BAFO",
        event: "SEND_BAFO",
        section: "offers",
      },
      {
        key: "acceptedOffers",
        label: "Accepted Offers",
        event: "ACCEPTED_OFFERS",
        section: "offers",
      },
      {
        key: "marginChange",
        label: "Margin Change",
        event: "MARGIN_CHANGE",
        section: "offers",
      },
      {
        key: "buyerViewOffers",
        label: "Buyer View offers",
        event: "BUYER_VIEW_OFFERS",
        section: "offers",
      },
      {
        key: "raiseRequirements",
        label: "Raise Requirements",
        event: "RAISE_REQUIREMENTS",
        section: "requirements",
      },
      {
        key: "raiseRFQs",
        label: "Raise RFQs",
        event: "RAISE_RFQS",
        section: "rfqs",
      },
      {
        key: "KYBStatusChanged",
        label: "KYB Status Changed",
        event: "KYB_STATUS_CHANGED",
        section: "offers",
      },
    ],
    []
  );

  const [notifications, setNotifications] = useState(
    notificationEvents.reduce((acc, item) => {
      acc[item.key] = true; // Default all to true
      return acc;
    }, {})
  );

  const [submitted, setSubmitted] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Get current user email
  useEffect(() => {
    const getUserEmail = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        setEmailID(user.attributes.email);
      } catch (error) {
        console.error("Error getting user email:", error);
      }
    };
    getUserEmail();
  }, []);

  // Load existing notification settings
  const loadNotificationSettings = useCallback(async () => {
    if (!emailID) return;

    setLoading(true);
    try {
      const body = {
        target: "listNotificationSettings",
        userOrder: "SettingID ASC",
        rowStart: 0,
        pageSize: 100,
        filterTerms: {
          EmailID: emailID,
        },
      };

      console.log("Loading Notification Settings for:", emailID);
      const result = await fetchData(body);

      if (result && (result.items || result.statusCode === 200)) {
        const savedSettings = result.items || [];
        console.log("Loaded Settings:", savedSettings);

        // Create a map of event -> status from saved settings
        const settingsMap = {};
        savedSettings.forEach((setting) => {
          settingsMap[setting.Event] = setting.Status === 1;
        });

        // Update notifications state based on saved settings or default to true
        const updatedNotifications = notificationEvents.reduce((acc, item) => {
          // If setting exists in DB, use that value; otherwise default to true (enabled)
          acc[item.key] = settingsMap.hasOwnProperty(item.event)
            ? settingsMap[item.event]
            : true;
          return acc;
        }, {});

        setNotifications(updatedNotifications);
      } else {
        console.log("No saved settings found, using defaults");
      }
    } catch (error) {
      console.error("Error loading notification settings:", error);
      // On error, keep default settings (all enabled)
    } finally {
      setLoading(false);
    }
  }, [emailID, notificationEvents, setLoading]);

  // Load settings when emailID is available
  useEffect(() => {
    if (emailID) {
      loadNotificationSettings();
    }
  }, [emailID, loadNotificationSettings]);

  const handleToggle = (event) => {
    setNotifications((prev) => ({
      ...prev,
      [event.target.name]: event.target.checked,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Build settings array with event and status
      const settingsData = notificationEvents.map((item) => ({
        Event: item.event,
        Status: notifications[item.key] ? 1 : 0,
        EmailID: emailID,
      }));

      const body = {
        target: "createNotificationSettings",
        settings: settingsData,
      };

      console.log("Submitting Notification Settings:", body);
      const response = await postQuery(body);

      if (response && response.statusCode === 200) {
        setSubmitted(true);
        setSnackbar({
          open: true,
          message: "Notification settings saved successfully!",
          severity: "success",
        });
      } else {
        throw new Error("Failed to save settings");
      }
    } catch (error) {
      console.error("Error submitting notification settings:", error);
      setSnackbar({
        open: true,
        message: "Failed to save notification settings. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderSwitch = (label, name) => (
    <FormControlLabel
      control={
        <IOSSwitch
          sx={{ m: 1 }}
          checked={notifications[name]}
          onChange={handleToggle}
          name={name}
        />
      }
      label={label}
      labelPlacement="start"
      sx={{
        justifyContent: "space-between",
        marginLeft: 0,
        width: "100%",
        mb: 0,
      }}
    />
  );

  return (
    <div className="container notification">
      <form onSubmit={handleSubmit}>
        <div className="card p-2">
          <div
            className="table-header"
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 5,
            }}
          >
            <div className="header-text">
              <div className="head-text">Notification</div>
              {/* <div className="head-subtext" style={{ color: "#666" }}>
                Change Notification.
              </div> */}
            </div>
            <div className="submit-btn-form">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {loading ? "Submitting..." : submitted ? "Submitted" : "Submit"}
              </Button>
            </div>
          </div>

          {/* Offer Management Notifications */}
          <Paper
            sx={{ p: 3, borderRadius: 2, mb: 3, backgroundColor: "#f9f9f9" }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Offer Management Notifications
            </Typography>
            {renderSwitch("Send Firm offers", "sendFirmOffers")}
            {renderSwitch("Shortlist", "shortlist")}
            {renderSwitch("Raise Query", "raiseQuery")}
            {renderSwitch("Send BAFO", "sendBAFO")}
            {renderSwitch("Accepted Offers", "acceptedOffers")}
            {renderSwitch("Margin Change", "marginChange")}
            {renderSwitch("Buyer View offers", "buyerViewOffers")}
          </Paper>

          {/* Requirement Notifications */}
          <Paper
            sx={{ p: 3, borderRadius: 2, mb: 3, backgroundColor: "#f9f9f9" }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Requirement Notifications
            </Typography>
            {renderSwitch("Raise Requirements", "raiseRequirements")}
          </Paper>

          {/* RFQ Notifications */}
          <Paper
            sx={{ p: 3, borderRadius: 2, mb: 3, backgroundColor: "#f9f9f9" }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              RFQ Notifications
            </Typography>
            {renderSwitch("Raise RFQs", "raiseRFQs")}
          </Paper>

          {/* KYB Notifications */}
          <Paper
            sx={{ p: 3, borderRadius: 2, mb: 3, backgroundColor: "#f9f9f9" }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              KYB Notifications
            </Typography>
            {renderSwitch("KYB Status Change", "KYBStatusChanged")}
          </Paper>
        </div>
      </form>

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Notification;
